import { useCallback } from "react";
import { permissionsSync } from "../../../components/PermissionsManagement/utils/permissionsSync";
import { getDependentPermissions } from "./utils/permissionCalculator";
import {
  getOriginalPermissionValue,
  findRawItemById,
} from "./utils/permissionsHelpers";
import { usePermissionsUpdater } from "./usePermissionsUpdater";

/**
 * Handles permission changes with all dependencies passed as props
 * This avoids circular dependencies while keeping concerns separated
 */
export const usePermissionChangeHandler = ({
  rawApiData,
  currentPermissions,
  trackedChanges,
  activeSubTab,
  formsContentType,
  updater,
  updatePermissions,
  setTrackedChanges,
  applySyncChange,
  getCurrentViewKey,
  getAllViewStates,
  getProcessedStructure,
}) => {
  // Create updaters for both tabs to handle sync properly
  const formsUpdater = usePermissionsUpdater("forms", rawApiData);
  const formsContentUpdater = usePermissionsUpdater(
    "forms_content",
    rawApiData
  );

  /**
   * Main permission change handler
   */
  const handlePermissionChange = useCallback(
    (categoryId, itemId, permissionType, newValue, options = {}) => {
      if (!currentPermissions) return;

      // Clone current permissions
      const updatedPermissions = JSON.parse(JSON.stringify(currentPermissions));

      // Apply the permission change
      const affectedItems = updater.updatePermissions(
        updatedPermissions,
        categoryId,
        itemId,
        permissionType,
        newValue,
        options
      );

      // Apply dependent permissions
      const dependentAffectedItems = applyDependentPermissions(
        updatedPermissions,
        affectedItems,
        permissionType,
        newValue
      );

      // Handle Tariff/Fees sync BEFORE building tracked changes
      const tariffFeesSyncItems = [];
      if (
        activeSubTab === "forms" &&
        (permissionType === "edit" || permissionType === "delete")
      ) {
        handleTariffFeesSync(
          updatedPermissions,
          affectedItems,
          permissionType,
          newValue,
          tariffFeesSyncItems
        );
      }

      // Calculate sync changes with the updated permissions
      const syncChanges = calculateSyncChanges(
        categoryId,
        itemId,
        permissionType,
        newValue,
        options,
        updatedPermissions,
        affectedItems
      );

      // Track all changes - include Tariff/Fees sync items
      const allAffectedItems = [
        ...affectedItems,
        ...dependentAffectedItems,
        ...tariffFeesSyncItems,
      ];

      const newTrackedChanges = buildTrackedChanges(
        trackedChanges,
        allAffectedItems,
        permissionType,
        newValue,
        categoryId,
        options
      );

      // Apply sync changes BEFORE updating state
      let finalTrackedChanges = newTrackedChanges;
      if (syncChanges.length > 0) {
        finalTrackedChanges = applySyncChangesToState(
          syncChanges,
          newTrackedChanges
        );
      }

      // Update state with the FINAL tracked changes size
      updatePermissions(updatedPermissions, finalTrackedChanges.size);
      setTrackedChanges(finalTrackedChanges);
    },
    [
      currentPermissions,
      trackedChanges,
      activeSubTab,
      formsContentType,
      rawApiData,
      updater,
      formsUpdater,
      formsContentUpdater,
      updatePermissions,
      setTrackedChanges,
      applySyncChange,
      getCurrentViewKey,
      getAllViewStates,
      getProcessedStructure,
    ]
  );

  /**
   * Apply dependent permission logic
   */
  const applyDependentPermissions = (
    updatedPermissions,
    affectedItems,
    changedPermissionType,
    newValue
  ) => {
    const dependentPermissions = getDependentPermissions(
      changedPermissionType,
      activeSubTab,
      formsContentType
    );

    const additionalAffectedItems = [];

    if (dependentPermissions.length === 0) {
      return additionalAffectedItems;
    }

    affectedItems.forEach(({ id: itemId, type }) => {
      const targetItem = findItemInPermissions(updatedPermissions, itemId);

      if (!targetItem?.permissions) return;

      dependentPermissions.forEach((depPermission) => {
        const shouldUpdate = shouldUpdateDependentPermission(
          changedPermissionType,
          depPermission,
          newValue
        );

        if (shouldUpdate) {
          targetItem.permissions[depPermission] = newValue;
          additionalAffectedItems.push({
            id: itemId,
            type: type,
            permissionType: depPermission,
            isDependent: true,
            expectedValue: newValue,
          });
        }
      });
    });

    // Handle special Tariff/Fees sync for Forms tab
    if (
      activeSubTab === "forms" &&
      (changedPermissionType === "edit" || changedPermissionType === "delete")
    ) {
      handleTariffFeesSync(
        updatedPermissions,
        affectedItems,
        changedPermissionType,
        newValue,
        additionalAffectedItems
      );
    }

    return additionalAffectedItems;
  };

  /**
   * Find an item in the permissions structure
   */
  const findItemInPermissions = (permissions, itemId) => {
    if (permissions.isFlat && permissions.items) {
      return permissions.items.find((item) => item.id === itemId);
    }

    if (permissions.categories) {
      for (const category of permissions.categories) {
        if (category.id === itemId) return category;

        for (const item of category.items || []) {
          if (item.id === itemId) return item;

          // Handle nested structures
          if (item.isParentGroup && item.items) {
            for (const form of item.items) {
              if (form.id === itemId) return form;
              if (form.items) {
                const field = form.items.find((f) => f.id === itemId);
                if (field) return field;
              }
            }
          } else if (item.items) {
            const nested = item.items.find((n) => n.id === itemId);
            if (nested) return nested;
          }
        }
      }
    }

    return null;
  };

  /**
   * Determine if a dependent permission should be updated
   */
  const shouldUpdateDependentPermission = (
    changedPermissionType,
    dependentPermission,
    newValue
  ) => {
    // Tools: edit always matches view_open
    if (
      activeSubTab === "tools" &&
      changedPermissionType === "view_open" &&
      dependentPermission === "edit"
    ) {
      return true;
    }

    // Forms Content tabs: edit and print depend on view
    if (
      activeSubTab === "forms_content" &&
      formsContentType === "tabs" &&
      changedPermissionType === "view" &&
      ["edit", "print"].includes(dependentPermission)
    ) {
      return !newValue; // Only uncheck when parent is unchecked
    }

    // Forms: dependent permissions when search_view is unchecked
    if (
      activeSubTab === "forms" &&
      changedPermissionType === "search_view" &&
      ["create", "edit", "delete", "print"].includes(dependentPermission)
    ) {
      return !newValue; // Only uncheck when parent is unchecked
    }

    // Generic case: uncheck dependent when parent is unchecked
    return !newValue;
  };

  /**
   * Handle Tariff/Fees synchronization
   */
  const handleTariffFeesSync = (
    updatedPermissions,
    affectedItems,
    changedPermissionType,
    newValue,
    additionalAffectedItems
  ) => {
    affectedItems.forEach(({ id: itemId }) => {
      const rawItem = findRawItemById(rawApiData, itemId);
      if (!rawItem) return;

      let syncItemId = null;

      if (rawItem.columnname === "FEES_FORM") {
        const tariffItem = rawApiData?.formS_TABLE?.find(
          (item) => item.columnname === "TARIFF_FORM"
        );
        syncItemId = tariffItem?.itemid;
      } else if (rawItem.columnname === "TARIFF_FORM") {
        const feesItem = rawApiData?.formS_TABLE?.find(
          (item) => item.columnname === "FEES_FORM"
        );
        syncItemId = feesItem?.itemid;
      }

      if (syncItemId) {
        const syncTargetItem = findItemInPermissions(
          updatedPermissions,
          syncItemId
        );
        if (syncTargetItem?.permissions) {
          syncTargetItem.permissions[changedPermissionType] = newValue;
          additionalAffectedItems.push({
            id: syncItemId,
            type: "item",
            permissionType: changedPermissionType,
            isSynchronized: true,
            expectedValue: newValue,
          });
        }
      }
    });
  };

  /**
   * Calculate sync changes between tabs - only Forms to Forms Content (excluding print)
   */
  const calculateSyncChanges = (
    categoryId,
    itemId,
    permissionType,
    newValue,
    options,
    updatedPermissions,
    affectedItems
  ) => {
    if (permissionsSync.isSyncInProgress()) {
      return [];
    }

    // Skip print permission sync from Forms to Forms Content
    if (activeSubTab === "forms" && permissionType === "print") {
      return [];
    }

    const syncChanges = [];
    const isCategoryChange = !itemId || itemId === categoryId;

    try {
      permissionsSync.setSyncing(true);

      if (isCategoryChange) {
        // Category-level sync
        if (activeSubTab === "forms") {
          ["forms_content_general", "forms_content_tabs"].forEach(
            (targetView) => {
              const contentType = targetView.includes("tabs")
                ? "tabs"
                : "general";
              const mappedPermType = permissionsSync.mapPermissionType(
                permissionType,
                activeSubTab,
                "forms_content"
              );

              if (mappedPermType) {
                syncChanges.push({
                  targetTab: "forms_content",
                  targetView,
                  categoryId,
                  itemId: null,
                  permissionType: mappedPermType,
                  newValue,
                  options: {
                    contentType,
                    fromFormsSync: true,
                    forceFullCascade: true, // Add this to ensure full cascade for category changes
                  },
                });
              }
            }
          );
        }
      } else {
        // Item-level sync
        if (activeSubTab === "forms") {
          // Check if this item has children in the Forms structure
          const clickedItem = findRawItemById(rawApiData, itemId);
          const hasChildren = rawApiData?.formS_TABLE?.some(
            (item) => item.parentitemid === itemId
          );

          if (hasChildren && clickedItem) {
            // This is a parent item like "Applications"
            // It needs to map to a parent group in Forms Content
            ["forms_content_general", "forms_content_tabs"].forEach(
              (targetView) => {
                const contentType = targetView.includes("tabs")
                  ? "tabs"
                  : "general";

                const targetStructure = getProcessedStructure(
                  "forms_content",
                  contentType
                );

                if (targetStructure) {
                  const mappedPermType = permissionsSync.mapPermissionType(
                    permissionType,
                    activeSubTab,
                    "forms_content"
                  );

                  if (mappedPermType) {
                    // Find the category that contains this parent group
                    let targetCategoryId = null;
                    const parentGroupId = `parent_${itemId}`;

                    for (const category of targetStructure.categories || []) {
                      const parentGroup = category.items?.find(
                        (item) => item.id === parentGroupId
                      );

                      if (parentGroup) {
                        targetCategoryId = category.id;
                        break;
                      }
                    }

                    if (targetCategoryId) {
                      // Sync the parent group with forceFullCascade flag
                      syncChanges.push({
                        targetTab: "forms_content",
                        targetView,
                        categoryId: targetCategoryId,
                        itemId: parentGroupId,
                        permissionType: mappedPermType,
                        newValue,
                        options: {
                          contentType,
                          fromFormsSync: true,
                          isParentGroup: true,
                          forceFullCascade: true, // This ensures all nested levels get updated
                        },
                      });
                    }
                  }
                }
              }
            );
          } else {
            // Regular item without children - existing sync logic
            ["forms_content_general", "forms_content_tabs"].forEach(
              (targetView) => {
                const contentType = targetView.includes("tabs")
                  ? "tabs"
                  : "general";
                const targetStructure = getProcessedStructure(
                  "forms_content",
                  contentType
                );

                if (targetStructure) {
                  const mappedPermType = permissionsSync.mapPermissionType(
                    permissionType,
                    activeSubTab,
                    "forms_content"
                  );

                  if (mappedPermType) {
                    const contentMatch =
                      permissionsSync.findContentItemFromForms(
                        itemId,
                        targetStructure
                      );

                    if (contentMatch) {
                      syncChanges.push({
                        targetTab: "forms_content",
                        targetView,
                        categoryId,
                        itemId: contentMatch.item.id,
                        permissionType: mappedPermType,
                        newValue,
                        options: {
                          contentType,
                          fromFormsSync: true,
                          ...options,
                        },
                      });
                    }
                  }
                }
              }
            );
          }
        }
      }
    } finally {
      permissionsSync.setSyncing(false);
    }

    return syncChanges;
  };

  /**
   * Apply sync changes to state - handles multiple target views
   */
  const applySyncChangesToState = (syncChanges, currentTrackedChanges) => {
    const newTrackedChanges = new Map(currentTrackedChanges);

    syncChanges.forEach((syncChange) => {
      const { targetView, targetTab } = syncChange;

      // Get the target structure based on targetView
      const contentType = targetView?.includes("tabs") ? "tabs" : "general";

      // Get the current modified state if it exists
      let targetStructure;
      const allStates = getAllViewStates();

      if (allStates[targetView]?.permissions) {
        targetStructure = allStates[targetView].permissions;
      } else {
        targetStructure = getProcessedStructure(
          targetTab,
          targetTab === "forms_content" ? contentType : null
        );
      }

      if (!targetStructure) {
        return;
      }

      const targetPermissions = JSON.parse(JSON.stringify(targetStructure));

      // Use the appropriate updater for the target tab
      const targetUpdater =
        syncChange.targetTab === "forms" ? formsUpdater : formsContentUpdater;

      // Apply the sync change
      const affectedItems = targetUpdater.updatePermissions(
        targetPermissions,
        syncChange.categoryId,
        syncChange.itemId,
        syncChange.permissionType,
        syncChange.newValue,
        {
          ...syncChange.options,
          fromFormsSync: syncChange.targetTab === "forms_content",
        }
      );

      // Apply sync to state and get tracked changes AND cleanup keys
      const syncResult = applySyncChange({
        ...syncChange,
        targetPermissions,
        existingTrackedChanges: newTrackedChanges,
        viewKey: targetView,
        rawApiData: rawApiData,
      });

      // Handle the returned tracked changes and cleanup
      if (syncResult) {
        // Add new tracked changes
        if (
          syncResult.trackedChanges &&
          Array.isArray(syncResult.trackedChanges)
        ) {
          syncResult.trackedChanges.forEach(({ changeKey, changeData }) => {
            newTrackedChanges.set(changeKey, changeData);
          });
        }

        // CRITICAL: Remove cleanup keys (items that returned to original value without manual changes)
        if (syncResult.cleanupKeys && Array.isArray(syncResult.cleanupKeys)) {
          syncResult.cleanupKeys.forEach((keyToRemove) => {
            if (newTrackedChanges.has(keyToRemove)) {
              newTrackedChanges.delete(keyToRemove);
            }
          });
        }
      }
    });

    return newTrackedChanges;
  };

  /**
   * Build tracked changes map
   */
  const buildTrackedChanges = (
    currentTrackedChanges,
    affectedItems,
    permissionType,
    newValue,
    categoryId,
    options
  ) => {
    const changes = new Map(currentTrackedChanges);
    const viewKey = getCurrentViewKey();

    // Check if this is a manual change in Forms Content
    const isManualFormsContentChange =
      activeSubTab === "forms_content" && !permissionsSync.isSyncInProgress();

    affectedItems.forEach(
      ({
        id: affectedId,
        type,
        permissionType: affectedPermType,
        expectedValue,
        syncValue,
        isRecalculated,
      }) => {
        // Skip tracking recalculated items (visual-only updates)
        if (isRecalculated) {
          return; // Don't track this change
        }
        const trackingPermType = affectedPermType || permissionType;
        const changeKey = `${viewKey}_${affectedId}_${trackingPermType}`;

        const originalValue = getOriginalPermissionValue(
          rawApiData,
          affectedId,
          trackingPermType
        );

        // Use syncValue for tracking if it's different from visual value (for Forms Content forms)
        const actualNewValue =
          syncValue !== undefined
            ? syncValue
            : expectedValue !== undefined
            ? expectedValue
            : newValue;

        // Get existing change to preserve flags
        const existingChange = changes.get(changeKey);

        // CRITICAL: Determine if we should track this change
        if (originalValue === actualNewValue) {
          // Value is back to original
          if (existingChange && existingChange.isFromSync) {
            // This was synced - keep it even though it's back to original

            changes.set(changeKey, {
              ...existingChange,
              currentValue: actualNewValue,
              timestamp: Date.now(),
              wasManuallyModified:
                isManualFormsContentChange ||
                existingChange.wasManuallyModified,
            });
          } else {
            // Not from sync and value is back to original - remove from tracking
            changes.delete(changeKey);
          }
        } else {
          // Value is different from original - track it
          changes.set(changeKey, {
            itemId: affectedId,
            categoryId: type === "category" ? null : categoryId,
            formId: options.formId,
            parentId: options.parentId,
            parentGroupId: options.parentGroupId,
            permissionType: trackingPermType,
            originalValue,
            currentValue: actualNewValue,
            timestamp: Date.now(),
            viewKey,
            subTab: activeSubTab,
            contentType: formsContentType,
            isDependent: false,
            isFromSync: existingChange?.isFromSync || false,
            wasManuallyModified:
              isManualFormsContentChange ||
              existingChange?.wasManuallyModified ||
              false,
          });
        }
      }
    );

    return changes;
  };

  return {
    handlePermissionChange,
  };
};
